import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  ShipDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type ShipDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderSelectControl,
  renderDateControl,
  renderTextControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// Re-export schema and types
export { ShipDeviceFormSchema, type ShipDeviceFormData } from './schema'

export type ShipDeviceComposerProps = {
  deviceNumber: number
  deviceName?: string
  deviceProcurementInfoNumber?: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof ShipDeviceFormData, value: unknown): string | null => {
  const shape = ShipDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function ShipDeviceComposer(props: ShipDeviceComposerProps) {
  const { deviceNumber, deviceProcurementInfoNumber, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

	const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
	const groups = FormConfigLoader.getGroups(FORM_CONFIG)

	// Fetch address options from API
	const { optionsMap, loadingMap } = useSelectOptionsMap({
		deviceShippedToAddress: {
			endpoint: 'getAddressListForDeviceProcurement',
			dataKey: 'addresses',
			valueField: 'addressNumber',
			labelField: 'addressName',
		},
	})

	// Fetch existing procurementInfo for merging (must be at top level)
	const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber });
	const deviceData = deviceDataRaw?.content;

	// Invalidate the device list AND the single-device query the detail page reads, so the
	// shipping details + new status show immediately instead of stale cached data.
	const shipDevice = useApiMutation('upsertDeviceProcurementInfo', 'POST', {
		invalidates: ['getDevices', 'getDeviceByNumber'],
		onError: (error) => {
			onError?.(error)
		},
	})
	const upsertDevice = useApiMutation('upsertDevice', 'POST', {
		invalidates: ['getDevices', 'getDeviceByNumber'],
		onError: (error) => {
			onError?.(error)
		},
	})

  const form = useAppForm({
    schema: ShipDeviceFormSchema,
    defaultValues: {
      shipperName: undefined as any,
      deviceShippingDate: '',
      deviceShippingTrackingNumber: undefined,
      deviceShippedToAddress: '',
    } as ShipDeviceFormData,
    onSubmit: async ({ value }) => {
      const formValue = value as ShipDeviceFormData
      // Wait for device data to load
      if (isDeviceLoading) return;

      try {
        // Build procurement info payload and set status to 'shipped'
        const { procurementInfo: newProcurementInfo } = transformToApiRequest(formValue, deviceNumber, deviceProcurementInfoNumber);
        // Merge with existing procurementInfo if available
        const existingProcurementInfo = deviceData?.procurementInfo || {};
        const mergedProcurementInfo = {
          ...existingProcurementInfo,
          ...newProcurementInfo,
          deviceProcurementStatus: 'shipped',
        };

        // Step 1: Update procurement info
        await shipDevice.mutateAsync({ deviceNumber, procurementInfo: mergedProcurementInfo });

        // Step 2: Set deviceStatus to 'shipped'.
        // Drop an empty deviceName — devices can be ordered without a name, and the backend
        // rejects deviceName "" (optional, but minLength 1 if present).
        const { deviceName: existingDeviceName, ...restDevice } = deviceData?.device ?? {};
        const mergedDevice = {
          ...restDevice,
          ...(existingDeviceName ? { deviceName: existingDeviceName } : {}),
          deviceNumber,
          deviceStatus: 'shipped',
        };
        await upsertDevice.mutateAsync({ device: mergedDevice });

        // All steps completed successfully - trigger refresh
        onSuccess?.();
      } catch (error) {
        // Errors already handled by mutation error handlers
        log.error('[shipDevice] Failed to ship device:', error);
      }
    },
  })

  const renderControl = (control: any) => {
    const isRequired = control.required

    if (control.htmlControl === 'select') {
      // Get options from API or YAML
      const options = control.optionsEndpoint 
        ? optionsMap[control.name] || []
        : FormConfigLoader.normalizeSelectOptions(control.selectOptions)
      
      const isLoading = loadingMap[control.name]

      return (
        <form.AppField 
          key={control.name}
          name={control.name}
          validators={{
            onBlur: ({ value }) =>
              validateField(control.name as keyof ShipDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: control.disabled ?? false,
              options: options,
              isLoading: isLoading,
            }
            return renderSelectControl(config, bound)
          }}
        </form.AppField>
      )
    }

    if (control.htmlControl === 'date') {
      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            onBlur: ({ value }) => validateField(control.name as keyof ShipDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: control.disabled ?? false,
              type: 'date'
            }
            return renderDateControl(config, bound)
          }}
        </form.AppField>
      )
    }

    // Default to text input
    return (
      <form.AppField 
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof ShipDeviceFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: 'text'
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        {groups.map((group) => {
          const groupControls = controlsByGroup.get(group.formGroupName) || []
          if (groupControls.length === 0) return null
          return (
            <div key={group.formGroupName} className="space-y-4">
              {groupControls.map(renderControl)}
            </div>
          )
        })}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}
          
          {renderButtonControl({
            label: (shipDevice.isPending || upsertDevice.isPending)
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Shipping...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Ship Device'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}
